/**
 * chatSuggestionUtils — pure helpers behind the suggestion showcase (empty
 * state + Ideas dialog). Groups the backend's suggested questions by category
 * and guarantees a CSV-export example is always on display.
 */
import type { SuggestedQuestion } from '../../services/chatService';

/** Display metadata per category. `tint` names a theme token, resolved by the component. */
export interface CategoryMeta {
  key: string;
  label: string;
  /** icon name resolved by the component's icon map */
  icon: string;
  order: number;
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  scheduling: { key: 'scheduling', label: 'Scheduling', icon: 'schedule', order: 1 },
  analytics: { key: 'analytics', label: 'Analytics', icon: 'analytics', order: 2 },
  data: { key: 'data', label: 'Lab data', icon: 'search', order: 3 },
  quotes: { key: 'quotes', label: 'Quotes', icon: 'quotes', order: 4 },
  exports: { key: 'exports', label: 'CSV exports', icon: 'download', order: 5 },
  feedback: { key: 'feedback', label: 'Feedback', icon: 'feedback', order: 6 },
  catalog: { key: 'catalog', label: 'Test catalogue', icon: 'catalog', order: 7 },
  documents: { key: 'documents', label: 'Reports & documents', icon: 'description', order: 8 },
  visualization: { key: 'visualization', label: 'Charts', icon: 'chart', order: 9 },
  help: { key: 'help', label: 'How-to', icon: 'help', order: 10 },
};

const FALLBACK_META: CategoryMeta = { key: 'data', label: 'Lab data', icon: 'search', order: 3 };

export function categoryMeta(category: string | undefined): CategoryMeta {
  return CATEGORY_META[category || ''] || FALLBACK_META;
}

/** Static export examples appended when the backend rotation missed the category. */
export const STATIC_EXPORT_EXAMPLES: SuggestedQuestion[] = [
  { text: 'Export everyone on vacation next week', icon: 'download', category: 'exports', isNew: true },
  { text: "Export next month's machine bookings", icon: 'download', category: 'exports', isNew: true },
];

/**
 * Guarantee at least one CSV-export example is present (B3 requirement: the
 * export capability must always surface). Pure — returns a new array only
 * when something was appended.
 */
export function ensureExportExample(suggestions: SuggestedQuestion[]): SuggestedQuestion[] {
  if (suggestions.some((s) => s.category === 'exports')) return suggestions;
  return [...suggestions, STATIC_EXPORT_EXAMPLES[0]];
}

export interface SuggestionGroup {
  meta: CategoryMeta;
  items: SuggestedQuestion[];
}

/**
 * Group suggestions by category in a stable display order. Unknown categories
 * fold into the fallback group rather than disappearing.
 */
export function groupSuggestions(suggestions: SuggestedQuestion[]): SuggestionGroup[] {
  const byKey = new Map<string, SuggestionGroup>();
  for (const s of suggestions) {
    const meta = categoryMeta(s.category);
    let group = byKey.get(meta.key);
    if (!group) {
      group = { meta, items: [] };
      byKey.set(meta.key, group);
    }
    group.items.push(s);
  }
  return [...byKey.values()].sort((a, b) => a.meta.order - b.meta.order);
}
